import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSiteContent } from "../context/SiteContentContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Save, Plus, Trash2, Palette, ImagePlus, ShieldAlert, Eye, EyeOff, PawPrint, Crown, Search, Circle, Megaphone, Volume2, Sparkles } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

function hexToChannels(hex) {
  const m = (hex || "").replace("#", "").match(/^([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  return m ? `${parseInt(m[1], 16)} ${parseInt(m[2], 16)} ${parseInt(m[3], 16)}` : null;
}

function LivePreview({ form }) {
  const t = form.theme || {};
  const vars = {};
  const c1 = hexToChannels(t.maroon); if (c1) vars["--maroon-rgb"] = c1;
  const c2 = hexToChannels(t.gold); if (c2) vars["--gold-rgb"] = c2;
  const c3 = hexToChannels(t.cream); if (c3) vars["--cream-rgb"] = c3;
  const bg = form.backgrounds?.[0]?.url;
  return (
    <div data-testid="site-preview" style={vars} className="rounded-2xl border-4 border-black overflow-hidden brand-shadow">
      {/* mock header */}
      <div className="flex items-center gap-3 px-4 h-14 border-b-4 border-black" style={{ background: t.maroon }}>
        {form.logo_url ? <img src={mediaUrl(form.logo_url)} alt="logo" className="h-9 w-auto" /> : <span className="text-spotter-cream font-bold">Spotter</span>}
        <div className="hidden sm:flex items-center gap-1 bg-spotter-cream border-2 border-black rounded px-2 py-1 text-xs text-spotter-maroon"><Search className="w-3 h-3" />Find members…</div>
        <div className="ml-auto flex items-center gap-1 text-spotter-cream text-xs font-bold"><Circle className="w-2.5 h-2.5" style={{ fill: "#2ecc71", color: "#2ecc71" }} />1 online</div>
      </div>
      {/* mock body over background */}
      <div className="p-5" style={bg ? { backgroundImage: `url(${mediaUrl(bg)})`, backgroundSize: "cover", backgroundPosition: "center" } : { background: "rgb(var(--cream-rgb))" }}>
        <div className="max-w-md mx-auto bg-white border-4 border-black rounded-2xl p-4 brand-shadow-sm">
          <div className="flex items-center gap-2 text-spotter-maroon font-display text-xl font-bold"><Crown className="w-5 h-5" style={{ color: t.gold }} />{form.welcome_popup_title || "Welcome to Spotter"}</div>
          <p className="text-sm mt-1 text-spotter-maroon font-heading">{form.slogan}</p>
          <button className="mt-3 text-sm font-heading font-bold text-spotter-cream border-2 border-black rounded px-3 py-1.5 inline-flex items-center gap-1" style={{ background: t.maroon }}><PawPrint className="w-4 h-4" style={{ color: t.gold }} />Sample button</button>
          <span className="ml-2 inline-block text-xs font-bold border-2 border-black rounded-full px-2 py-0.5" style={{ background: t.gold }}>Gold accent</span>
        </div>
      </div>
      {/* mock footer */}
      <div className="text-center py-3" style={{ background: t.maroon }}>
        <span className="inline-block bg-black/70 text-spotter-cream rounded-full px-4 py-1 text-xs">{form.footer_text || "© Spotter"}</span>
      </div>
    </div>
  );
}

function SiteCard({ title, children, icon: Icon }) {
  return (
    <div className="bg-white border-4 border-black rounded-2xl p-4 brand-shadow space-y-3">
      <h3 className="font-display text-xl font-bold text-spotter-maroon flex items-center gap-2">{Icon && <Icon className="w-5 h-5" style={{ color: "#E19C00" }} />}{title}</h3>
      {children}
    </div>
  );
}

export default function SiteEditor() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { content, setContent, refresh } = useSiteContent();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newBg, setNewBg] = useState({ label: "", url: "" });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const fileRef = useRef();
  const logoRef = useRef();
  const spotlightPdfRef = useRef();

  useEffect(() => {
    if (!user?.is_admin) { navigate("/feed"); return; }
    setForm(JSON.parse(JSON.stringify({
      slogan: content.slogan || "",
      welcome_popup_title: content.welcome_popup_title || "",
      welcome_popup_body: content.welcome_popup_body || "",
      about_owner_title: content.about_owner_title || "",
      about_owner_body: content.about_owner_body || "",
      theme: content.theme || { maroon: "#520202", gold: "#E19C00", cream: "#FFFFED" },
      backgrounds: content.backgrounds || [],
      background_disclaimer: content.background_disclaimer || "",
      reporting_policy: content.reporting_policy || "",
      community_rule: content.community_rule || "",
      landing_headline: content.landing_headline || "",
      login_updates: content.login_updates || "",
      footer_text: content.footer_text || "",
      logo_url: content.logo_url || "",
      notification_sound_url: content.notification_sound_url || "",
      premium_popup_enabled: content.premium_popup_enabled !== false,
      premium_popup_title: content.premium_popup_title || "",
      premium_popup_body: content.premium_popup_body || "",
      donation_banner_enabled: content.donation_banner_enabled !== false,
      donation_banner_text: content.donation_banner_text || "",
      donation_paypal_url: content.donation_paypal_url || "",
      donation_venmo_url: content.donation_venmo_url || "",
      spotlight_den_enabled: content.spotlight_den_enabled !== false,
      spotlight_den_title: content.spotlight_den_title || "",
      spotlight_den_pdf_url: content.spotlight_den_pdf_url || "",
    })));
  }, [content, user, navigate]);

  if (!form) return <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-spotter-maroon" /></div>;

  const uploadBg = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setNewBg((b) => ({ ...b, url: data.url }));
      toast.success("Photo uploaded — add a name and save");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const addBg = () => {
    if (!newBg.url) { toast.error("Upload or paste an image URL first."); return; }
    setForm((p) => ({ ...p, backgrounds: [...p.backgrounds, { key: `bg_${Date.now()}`, label: newBg.label || "Background", url: newBg.url }] }));
    setNewBg({ label: "", url: "" });
  };
  const removeBg = (i) => setForm((p) => ({ ...p, backgrounds: p.backgrounds.filter((_, j) => j !== i) }));

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, logo_url: data.url }));
      toast.success("Logo uploaded — remember to Save");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploadingLogo(false); e.target.value = ""; }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/site-content", form);
      setContent((c) => ({ ...c, ...data }));
      await refresh();
      toast.success("Site content saved!");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploadSpotlightPdf = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.type !== "application/pdf") { toast.error("Please choose a PDF file."); return; }
    toast.info("Uploading & rendering PDF… this can take a moment.");
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/spotlight-den/upload-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, spotlight_den_pdf_url: data.pdf_url, spotlight_den_images: data.images || [] }));
      const linkCount = (data.images || []).reduce((n, im) => n + (im.links || []).length, 0);
      toast.success(`PDF ready — ${data.images?.length || 0} page(s), ${linkCount} clickable link(s). Remember to Save.`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { e.target.value = ""; }
  };

  const Card = SiteCard;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="site-editor">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="font-display text-3xl font-bold text-spotter-maroon">Site Content Editor</h2>
        <div className="flex items-center gap-2">
          <Button data-testid="toggle-preview" variant="outline" onClick={() => setShowPreview((p) => !p)} className="border-2 border-black gap-2 bg-white">{showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}{showPreview ? "Hide preview" : "Preview"}</Button>
          <Button data-testid="site-save-top" onClick={save} disabled={saving} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-2">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Save</Button>
        </div>
      </div>
      <p className="text-sm text-spotter-maroon -mt-3">Edit your site's text, slogan, welcome message, theme colors and profile background photos. Changes go live for everyone.</p>

      {showPreview && (
        <div className="space-y-2" data-testid="preview-wrap">
          <Label className="text-spotter-maroon font-bold flex items-center gap-1"><Eye className="w-4 h-4" />Live preview (unsaved changes)</Label>
          <LivePreview form={form} />
        </div>
      )}

      <Card title="Slogan & Welcome popup" icon={Palette}>
        <div><Label>Slogan</Label><Input data-testid="edit-slogan" value={form.slogan} onChange={(e) => setForm({ ...form, slogan: e.target.value })} className="border-2 border-black" /></div>
        <div><Label>Welcome popup title</Label><Input data-testid="edit-popup-title" value={form.welcome_popup_title} onChange={(e) => setForm({ ...form, welcome_popup_title: e.target.value })} className="border-2 border-black" /></div>
        <div><Label>Welcome popup body (blank line between paragraphs)</Label><Textarea data-testid="edit-popup-body" value={form.welcome_popup_body} onChange={(e) => setForm({ ...form, welcome_popup_body: e.target.value })} className="border-2 border-black min-h-[140px]" /></div>
      </Card>

      <Card title="About Us page">
        <div><Label>Heading</Label><Input data-testid="edit-about-title" value={form.about_owner_title} onChange={(e) => setForm({ ...form, about_owner_title: e.target.value })} className="border-2 border-black" /></div>
        <div><Label>About text (blank line between paragraphs)</Label><Textarea data-testid="edit-about-body" value={form.about_owner_body} onChange={(e) => setForm({ ...form, about_owner_body: e.target.value })} className="border-2 border-black min-h-[140px]" /></div>
        <div><Label>Reporting policy disclaimer (shown under the About text)</Label><Textarea data-testid="edit-reporting-policy" value={form.reporting_policy} onChange={(e) => setForm({ ...form, reporting_policy: e.target.value })} className="border-2 border-black min-h-[100px]" /></div>
        <div><Label>Community Rule (shown under the reporting section — first line is the heading)</Label><Textarea data-testid="edit-community-rule" value={form.community_rule} onChange={(e) => setForm({ ...form, community_rule: e.target.value })} className="border-2 border-black min-h-[160px]" /></div>
        <div><Label>Homepage headline (shown to logged-out visitors)</Label><Textarea data-testid="edit-landing-headline" value={form.landing_headline || ""} onChange={(e) => setForm({ ...form, landing_headline: e.target.value })} className="border-2 border-black min-h-[60px]" /></div>
        <div><Label>Login page updates / announcement</Label><Textarea data-testid="edit-login-updates" value={form.login_updates || ""} onChange={(e) => setForm({ ...form, login_updates: e.target.value })} className="border-2 border-black min-h-[100px]" /></div>
      </Card>

      <Card title="Premium Memberships popup (over The Prowl)" icon={Megaphone}>
        <div className="flex items-center justify-between bg-spotter-cream border-2 border-black rounded-lg p-3">
          <Label className="font-bold">Show the premium popup to members</Label>
          <Switch data-testid="edit-premium-enabled" checked={!!form.premium_popup_enabled} onCheckedChange={(v) => setForm({ ...form, premium_popup_enabled: v })} />
        </div>
        <div><Label>Popup title</Label><Input data-testid="edit-premium-title" value={form.premium_popup_title} onChange={(e) => setForm({ ...form, premium_popup_title: e.target.value })} className="border-2 border-black" /></div>
        <div><Label>Popup body (blank line between paragraphs)</Label><Textarea data-testid="edit-premium-body" value={form.premium_popup_body} onChange={(e) => setForm({ ...form, premium_popup_body: e.target.value })} className="border-2 border-black min-h-[260px] font-mono text-xs" /></div>
        <p className="text-xs text-spotter-maroon italic">Members see this once. Editing the text shows it again to everyone.</p>
      </Card>

      <Card title="Donation banner (top of The Prowl)" icon={Megaphone}>
        <div className="flex items-center justify-between bg-spotter-cream border-2 border-black rounded-lg p-3">
          <Label className="font-bold">Show the donation banner</Label>
          <Switch data-testid="edit-donation-enabled" checked={!!form.donation_banner_enabled} onCheckedChange={(v) => setForm({ ...form, donation_banner_enabled: v })} />
        </div>
        <div><Label>Banner text</Label><Textarea data-testid="edit-donation-text" value={form.donation_banner_text} onChange={(e) => setForm({ ...form, donation_banner_text: e.target.value })} className="border-2 border-black min-h-[120px]" /></div>
        <div><Label>PayPal link (PayPal.me URL)</Label><Input data-testid="edit-donation-paypal" value={form.donation_paypal_url} onChange={(e) => setForm({ ...form, donation_paypal_url: e.target.value })} className="border-2 border-black" placeholder="https://paypal.me/yourname" /></div>
        <div><Label>Venmo link</Label><Input data-testid="edit-donation-venmo" value={form.donation_venmo_url} onChange={(e) => setForm({ ...form, donation_venmo_url: e.target.value })} className="border-2 border-black" placeholder="https://venmo.com/u/yourname" /></div>
      </Card>

      <Card title="Spotlight Den (business showcase)" icon={Sparkles}>
        <div className="flex items-center justify-between bg-spotter-cream border-2 border-black rounded-lg p-3">
          <Label className="font-bold">Show the Spotlight Den page</Label>
          <Switch data-testid="edit-spotlight-enabled" checked={!!form.spotlight_den_enabled} onCheckedChange={(v) => setForm({ ...form, spotlight_den_enabled: v })} />
        </div>
        <div><Label>Page title</Label><Input data-testid="edit-spotlight-title" value={form.spotlight_den_title} onChange={(e) => setForm({ ...form, spotlight_den_title: e.target.value })} className="border-2 border-black" /></div>
        <div>
          <Label>Spotlight Den PDF</Label>
          <p className="text-xs text-spotter-maroon mb-1">Upload a PDF — we render it as an image and keep its embedded links clickable. {(form.spotlight_den_images || []).length > 0 && `Currently ${(form.spotlight_den_images || []).length} page(s), ${(form.spotlight_den_images || []).reduce((n, im) => n + (im.links || []).length, 0)} link(s).`}</p>
          <div className="flex flex-wrap items-center gap-2">
            <input ref={spotlightPdfRef} type="file" accept="application/pdf" hidden onChange={uploadSpotlightPdf} />
            <Button type="button" variant="outline" data-testid="spotlight-pdf-upload-btn" onClick={() => spotlightPdfRef.current?.click()} className="border-2 border-black gap-2 bg-white"><ImagePlus className="w-4 h-4" />Upload PDF</Button>
            {form.spotlight_den_pdf_url && (
              <a href={mediaUrl(form.spotlight_den_pdf_url)} target="_blank" rel="noopener noreferrer" data-testid="spotlight-pdf-current" className="text-sm text-spotter-maroon font-semibold underline">View current PDF</a>
            )}
          </div>
          <Input data-testid="edit-spotlight-pdf-url" value={form.spotlight_den_pdf_url} onChange={(e) => setForm({ ...form, spotlight_den_pdf_url: e.target.value })} placeholder="or paste a PDF URL" className="border-2 border-black mt-2" />
        </div>
      </Card>


      <Card title="Default notification sound" icon={Volume2}>
        <p className="text-xs text-spotter-maroon">Site-wide default played for notifications &amp; messages. Members can upload their own in Settings to override this.</p>
        <div><Label>Default sound URL</Label><Input data-testid="edit-notif-sound" value={form.notification_sound_url} onChange={(e) => setForm({ ...form, notification_sound_url: e.target.value })} className="border-2 border-black" placeholder="https://…mp3" /></div>
      </Card>

      <Card title="Theme accent colors" icon={Palette}>
        <div className="grid grid-cols-3 gap-3">
          {["maroon", "gold", "cream"].map((k) => (
            <div key={k}>
              <Label className="capitalize">{k}</Label>
              <div className="flex items-center gap-2">
                <input data-testid={`theme-${k}`} type="color" value={form.theme[k]} onChange={(e) => setForm({ ...form, theme: { ...form.theme, [k]: e.target.value } })} className="w-12 h-10 border-2 border-black rounded cursor-pointer" />
                <Input value={form.theme[k]} onChange={(e) => setForm({ ...form, theme: { ...form.theme, [k]: e.target.value } })} className="border-2 border-black h-10" />
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Profile background photos" icon={ImagePlus}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {form.backgrounds.map((b, i) => (
            <div key={b.key || i} data-testid={`bg-item-${i}`} className="relative border-2 border-black rounded-lg overflow-hidden">
              <div className="h-20" style={{ backgroundImage: `url(${mediaUrl(b.url)})`, backgroundSize: "cover", backgroundPosition: "center" }} />
              <div className="text-xs font-bold text-center py-1 bg-spotter-cream truncate px-1">{b.label}</div>
              <button data-testid={`bg-remove-${i}`} onClick={() => removeBg(i)} className="absolute top-1 right-1 bg-black text-white rounded-full p-1"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
        <div className="border-2 border-dashed border-black rounded-lg p-3 space-y-2 bg-spotter-cream/50">
          <Label className="font-bold">Add a background photo</Label>
          <div className="flex flex-wrap gap-2 items-center">
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadBg} />
            <Button type="button" variant="outline" data-testid="bg-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="border-2 border-black gap-2 bg-white">{uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}Upload</Button>
            <Input data-testid="bg-new-label" value={newBg.label} onChange={(e) => setNewBg({ ...newBg, label: e.target.value })} placeholder="Name (e.g. Galaxy)" className="border-2 border-black h-10 w-40" />
            <Input data-testid="bg-new-url" value={newBg.url} onChange={(e) => setNewBg({ ...newBg, url: e.target.value })} placeholder="or paste image URL" className="border-2 border-black h-10 flex-1 min-w-[160px]" />
            <Button type="button" data-testid="bg-add-btn" onClick={addBg} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-1"><Plus className="w-4 h-4" />Add</Button>
          </div>
        </div>
        <div><Label className="flex items-center gap-1"><ShieldAlert className="w-4 h-4" />Copyright disclaimer (shown to members)</Label>
          <Textarea data-testid="edit-bg-disclaimer" value={form.background_disclaimer} onChange={(e) => setForm({ ...form, background_disclaimer: e.target.value })} className="border-2 border-black" /></div>
      </Card>

      <Card title="Footer & Logo" icon={ImagePlus}>
        <div><Label>Footer text</Label><Input data-testid="edit-footer-text" value={form.footer_text} onChange={(e) => setForm({ ...form, footer_text: e.target.value })} className="border-2 border-black" /></div>
        <div>
          <Label>Site logo</Label>
          <div className="flex items-center gap-3 mt-1">
            <div className="h-14 w-40 border-2 border-black rounded bg-spotter-maroon flex items-center justify-center overflow-hidden">
              {form.logo_url ? <img src={mediaUrl(form.logo_url)} alt="logo" className="h-full w-auto object-contain" data-testid="logo-preview" /> : <span className="text-spotter-cream text-xs">No logo</span>}
            </div>
            <input ref={logoRef} type="file" accept="image/*" hidden onChange={uploadLogo} />
            <Button type="button" variant="outline" data-testid="logo-upload-btn" onClick={() => logoRef.current?.click()} disabled={uploadingLogo} className="border-2 border-black gap-2 bg-white">{uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}Upload logo</Button>
          </div>
          <Input data-testid="edit-logo-url" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="or paste a logo image URL" className="border-2 border-black mt-2" />
        </div>
      </Card>

      <div className="flex justify-end pb-6">
        <Button data-testid="site-save-bottom" onClick={save} disabled={saving} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-2">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Save all changes</Button>
      </div>
    </div>
  );
}
